import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { usePolling } from "tap/utils/hooks/usePolling.js";
import { Row, Col } from "antd";
import Select from "@dtci/spellbook/Select";
import DataGrid from "@dtci/spellbook/DataGrid";
import Input from "@dtci/spellbook/Input";
import { InputLabel } from "tap/components/InputLabel";
import { tapApi } from "tap/api.js";
import {
  normalizeLicensingJobViewTask,
  normalizeLicensingJobViewTasks,
} from "tap/utils/normalizeLicensingJobViewTask.js";
import styles from "tap/components/AGGrid/details-table.module.scss";
import * as columnDefs from "tap/components/AGGrid/config/columns-def";

/**
 * Shared JobViewGrid Component
 *
 * Displays job tasks in a modal grid with polling support.
 * Used by TAPJobs, UpdateEngine, and LicensingJobs pages.
 *
 * @param {Object} props
 * @param {boolean} props.isJobViewModalOpen - Whether the modal is open
 * @param {Object} props.jobViewFilterForm - Filter form state
 * @param {Array} props.jobTasks - Array of job tasks
 * @param {Object} props.selectedJob - Currently selected job
 * @param {Array} props.taskStatusOptions - Options for task status dropdown
 * @param {Array} props.pendingStatuses - Array of statuses considered "pending"
 * @param {Function} props.updateStore - Function to update the store
 * @param {Function} props.updateSearchForm - Function to update the search form
 * @param {boolean} props.isLicensingJob - Whether this is a licensing job (affects API calls)
 * @param {string} props.columnDefsKey - Key for column definitions ('jobViewTasks' or 'licensingJobTasks')
 * @param {string} props.tasksFieldName - Field name for tasks in store ('jobViewTasks' or 'jobTasks')
 * @param {number} [props.currentPage=0] - Current zero-based page index for paginated task fetches
 */
export const JobViewGrid = ({
  isJobViewModalOpen,
  jobViewFilterForm,
  jobTasks,
  selectedJob,
  taskStatusOptions,
  pendingStatuses,
  updateStore,
  updateSearchForm,
  isLicensingJob = false,
  columnDefsKey = "jobViewTasks",
  tasksFieldName = "jobViewTasks",
  itemsPerPage = 20,
  currentPage = 0,
}) => {
  const agGridApi = useAgGridApi();
  const [searchText, setSearchText] = useState("");
  const hasFetchedRef = useRef(false);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();

  useEffect(() => {
    if (!isJobViewModalOpen) {
      hasFetchedRef.current = false;
    }
  }, [isJobViewModalOpen]);

  const filteredTasks = useMemo(() => {
    const tasks = (jobTasks || []).map((task) =>
      isLicensingJob ? normalizeLicensingJobViewTask(task) : task,
    );

    return tasks
      .slice()
      .sort((a, b) => a.taskDetailsId - b.taskDetailsId)
      .filter((task) =>
        Object.values(task)
          .filter(
            (value) => typeof value === "string" || typeof value === "number",
          )
          .some((value) =>
            value.toString().toLowerCase().includes(searchText.toLowerCase()),
          ),
      );
  }, [jobTasks, searchText, isLicensingJob]);

  const fetchJobTasks = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoadingTrue();
      }
      try {
        const [job, data] = await Promise.all([
          tapApi.job(selectedJob?.id, isLicensingJob),
          tapApi.jobViewTasks(
            selectedJob?.id,
            isLicensingJob,
            currentPage + 1,
            itemsPerPage,
            jobViewFilterForm.taskStatus.value,
          ),
        ]);

        if (
          data?.code ||
          data?.httpCode ||
          data?.error ||
          job?.code ||
          job?.httpCode ||
          job?.error
        ) {
          if (!silent) {
            showErrorToast(
              data?.message || job?.message || "Failed to fetch job tasks data.",
            );
          }
          return;
        }
        const tasks = isLicensingJob
          ? normalizeLicensingJobViewTasks(data?.tasks)
          : data?.tasks;

        updateStore({
          [tasksFieldName]: tasks,
          selectedJob: job,
          jobViewTotalPages: data?.totalPages,
          jobViewTotalItems: data?.totalElements ?? data?.tasks?.length ?? 0,
          jobViewCurrentPage: currentPage,
        });
      } catch (error) {
        if (!silent) {
          showErrorToast(error?.message || "Failed to fetch job tasks data.");
        }
        console.error("Error fetching job tasks:", error);
      } finally {
        if (!silent) {
          setLoadingFalse();
        }
      }
    },
    [
      selectedJob?.id,
      isLicensingJob,
      jobViewFilterForm.taskStatus.value,
      itemsPerPage,
      currentPage,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
      updateStore,
      tasksFieldName,
    ],
  );

  const pollJobTasks = useCallback(async () => {
    const silent = hasFetchedRef.current;
    hasFetchedRef.current = true;
    await fetchJobTasks(silent);
  }, [fetchJobTasks]);

  const isPending = pendingStatuses.includes(selectedJob?.jobStatus);

  usePolling(pollJobTasks, isJobViewModalOpen, isPending, 2000, [
    jobViewFilterForm.taskStatus.value,
    selectedJob?.jobStatus,
    currentPage,
    itemsPerPage,
  ]);

  const handleInputChange = (field, value) => {
    updateSearchForm({
      key: "jobViewFilterForm",
      data: { [field]: value },
    });
    if (field === "taskStatus") {
      updateStore({ jobViewCurrentPage: 0 });
    }
  };

  return (
    <>
      <Row>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Job ID">
            <Input
              value={selectedJob?.id ?? ""}
              width="medium"
              id="jobId"
              name="jobId"
              disabled
            />
          </InputLabel>
        </Col>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Job Status">
            <Input
              value={selectedJob?.jobStatus ?? ""}
              width="medium"
              id="jobStatus"
              name="jobStatus"
              disabled
            />
          </InputLabel>
        </Col>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Deals Processed">
            <Input
              value={selectedJob?.progressCount ?? ""}
              width="medium"
              id="progressCount"
              name="progressCount"
              disabled
            />
          </InputLabel>
        </Col>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Search">
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              width="medium"
              id="searchText"
              name="searchText"
              placeholder="Enter"
            />
          </InputLabel>
        </Col>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Task Status">
            <Select
              options={taskStatusOptions}
              onChange={(value) => handleInputChange("taskStatus", value)}
              value={jobViewFilterForm.taskStatus}
            />
          </InputLabel>
        </Col>
      </Row>
      <DataGrid
        className={styles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: filteredTasks ?? [],
          headerHeight: 45,
          suppressColumnStateReset: true,
          defaultColDef: {
            wrapText: true,
            autoHeight: true,
            resizable: true,
            suppressHeaderMenuButton: true,
            cellStyle: {
              paddingTop: "10px",
              paddingBottom: "10px",
              wordBreak: "break-word",
            },
          },
          getRowId: (params) => String(params.data.taskDetailsId),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columnDefs[columnDefsKey]()["default"]}
        sortable={false}
        striped
        noResultsMessage={"No Job Tasks Found"}
      />
    </>
  );
};
